/**
 * 認証済みユーザー向けパスワード API（FE 先行定義）。
 *
 * - GET  /v2/auth/password/policy … 所属 org 横断の実効ポリシー + description
 * - POST /v2/auth/password        … パスワード設定・変更（AAL1 freshness）
 *
 * NestJS SharedController（非 oRPC）だが、OpenAPILink は path/method で REST 呼び出し可能なため
 * ローカル Contract として扱う。OpenAPI schema / api-contract publish 後に差し替え可。
 *
 * BE の `DressCodeApiController` は `Controller({ version: '2' })` で URI バージョニングを行うため、
 * コントローラーが `@Get('/auth/password/policy')` でも実 URL は `/v2` 配下になる
 * （openspec のドキュメントは `/v2` 無しで記載されているが誤り）。
 *
 * レスポンス schema は招待経路と共有する（BE も同じ schema を DTO に `implements`）。
 */
import { oc } from '@orpc/contract';
import { EffectivePasswordPolicySchema } from '@standandforce/api-contract';
import { z } from 'zod';
export const UpdatePasswordInputSchema = z.object({
  newPassword: z.string().min(1)
});
export const authPasswordContract = oc.router({
  getEffectivePolicy: oc.route({
    method: 'GET',
    path: '/v2/auth/password/policy'
  }).input(z.object({})).output(EffectivePasswordPolicySchema),
  updatePassword: oc.route({
    method: 'POST',
    path: '/v2/auth/password'
  }).input(UpdatePasswordInputSchema).output(z.void())
});
export type EffectivePasswordPolicy = z.infer<typeof EffectivePasswordPolicySchema>;

/** ポリシーを取得できない未認証導線（Ory new-password 等）向けのフォールバック。 */
export const DEFAULT_PASSWORD_POLICY = {
  minLength: 10,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSymbol: true
} as const satisfies Pick<EffectivePasswordPolicy, 'minLength' | 'requireUppercase' | 'requireLowercase' | 'requireNumber' | 'requireSymbol'>;

/** クライアントで判定できる要件（漏洩照合はサーバ側のみのため含めない）。 */
export type PasswordPolicyRequirements = Pick<EffectivePasswordPolicy, 'minLength' | 'requireUppercase' | 'requireLowercase' | 'requireNumber' | 'requireSymbol'>;

/** 要件チェックリストの表示に渡すポリシー。漏洩照合は判定せず注記だけ出す。 */
export type PasswordPolicyDisplay = PasswordPolicyRequirements & Partial<Pick<EffectivePasswordPolicy, 'leakedPasswordCheckEnabled'>>;

/** BE evaluator と同じ文字種判定（FE リアルタイムチェック用）。 */
export const PASSWORD_CHARACTER_CLASS_PATTERN = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  symbol: /[^a-zA-Z0-9]/
} as const;
export type PasswordRequirementCheck = {
  key: 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'symbol';
  met: boolean;
};

/** 入力に対する要件チェックリスト（HIBP はサーバ側のみのため含めない）。 */
export function evaluatePasswordRequirements(password: string, policy: PasswordPolicyRequirements): PasswordRequirementCheck[] {
  const checks: PasswordRequirementCheck[] = [{
    key: 'minLength',
    met: password.length >= policy.minLength
  }];
  if (policy.requireUppercase) {
    checks.push({
      key: 'uppercase',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.uppercase.test(password)
    });
  }
  if (policy.requireLowercase) {
    checks.push({
      key: 'lowercase',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.lowercase.test(password)
    });
  }
  if (policy.requireNumber) {
    checks.push({
      key: 'number',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.number.test(password)
    });
  }
  if (policy.requireSymbol) {
    checks.push({
      key: 'symbol',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.symbol.test(password)
    });
  }
  return checks;
}
export function areAllPasswordRequirementsMet(password: string, policy: PasswordPolicyRequirements): boolean {
  return evaluatePasswordRequirements(password, policy).every(check => check.met);
}